// src/services/chat/chatLocalDb.js

import { openDB } from 'idb';
import { ChatMessage, MessageStatus, MessageType } from '../../models/chatModels/chatMessage';
import { ChatConversation } from '../../models/chatModels/chatConversation';

class ChatLocalDb {
  constructor() {
    this._db = null;
    this._dbName = 'filatom_chat_db';
    this._dbVersion = 5;
    this._cacheInitialized = false;
    this._conversationsCache = [];
    this._messagesCache = new Map(); // conversationId -> messages[]
  }

  static getInstance() {
    if (!ChatLocalDb._instance) {
      ChatLocalDb._instance = new ChatLocalDb();
    }
    return ChatLocalDb._instance;
  }

  // Use a method instead of getter for compatibility
  async getDb() {
    if (!this._db) {
      this._db = await this._open();
    }
    return this._db;
  }

  // ── Open / migrate ─────────────────────────────────────────────────────────

  async _open() {
    return openDB(this._dbName, this._dbVersion, {
      upgrade: async (db, oldVersion, newVersion, transaction) => {
        if (oldVersion < 1) {
          const messageStore = db.createObjectStore('messages', { keyPath: 'id' });
          messageStore.createIndex('conversationId', 'conversationId');
          messageStore.createIndex('conversationId_timestamp', ['conversationId', 'timestamp']);

          const convStore = db.createObjectStore('conversations', { keyPath: 'id' });
          convStore.createIndex('lastMessageTime', 'lastMessageTime');
          convStore.createIndex('isDeleted', 'isDeleted');
          convStore.createIndex('isArchived', 'isArchived');

          db.createObjectStore('user_presence', { keyPath: 'uid' });
        }
        if (oldVersion < 2) {
          // Add isDeleted column - handled by schema in write
        }
        if (oldVersion < 3) {
          // Add isArchived
        }
        if (oldVersion < 4) {
          // Add new message fields - handled by schema in write
        }
        if (oldVersion < 5) {
          if (!db.objectStoreNames.contains('user_presence')) {
            db.createObjectStore('user_presence', { keyPath: 'uid' });
          }
        }
      },
    });
  }

  // ── Messages ───────────────────────────────────────────────────────────────

  async saveMessages(messages) {
    if (!messages || messages.length === 0) return;
    const database = await this.getDb();
    const tx = database.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');

    // Update cache
    const conversationId = messages[0]?.conversationId;
    if (conversationId) {
      const cached = this._messagesCache.get(conversationId) || [];
      for (const m of messages) {
        const existingIndex = cached.findIndex(cm => cm.id === m.id);
        if (existingIndex >= 0) {
          cached[existingIndex] = m;
        } else {
          cached.push(m);
        }
      }
      // Sort by timestamp
      cached.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      this._messagesCache.set(conversationId, cached);
    }

    for (const m of messages) {
      await store.put(this._messageToRow(m));
    }

    await tx.done;
  }

  async saveMessage(message) {
    await this.saveMessages([message]);
  }

  async updateMessageStatus(messageId, status) {
    const database = await this.getDb();
    const tx = database.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    
    const message = await store.get(messageId);
    if (message) {
      message.status = status;
      await store.put(message);
      
      // Update cache
      for (const [convId, messages] of this._messagesCache) {
        const msgIndex = messages.findIndex(m => m.id === messageId);
        if (msgIndex >= 0) {
          messages[msgIndex].status = status;
          break;
        }
      }
    }
    
    await tx.done;
  }

  async updateMessageSeenBy(messageId, seenBy) {
    const database = await this.getDb();
    const tx = database.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    
    const message = await store.get(messageId);
    if (message) {
      message.seenBy = seenBy.join(',');
      message.status = 'read';
      await store.put(message);
      
      // Update cache
      for (const [convId, messages] of this._messagesCache) {
        const msgIndex = messages.findIndex(m => m.id === messageId);
        if (msgIndex >= 0) {
          messages[msgIndex].status = 'read';
          messages[msgIndex].seenBy = seenBy;
          break;
        }
      }
    }
    
    await tx.done;
  }

  async loadMessages(conversationId) {
    // Check cache first for instant response
    if (this._messagesCache.has(conversationId)) {
      return this._messagesCache.get(conversationId);
    }

    const database = await this.getDb();
    const tx = database.transaction('messages', 'readonly');
    const store = tx.objectStore('messages');
    const index = store.index('conversationId_timestamp');
    
    const range = IDBKeyRange.bound(
      [conversationId, 0],
      [conversationId, Number.MAX_SAFE_INTEGER]
    );
    
    const rows = await index.getAll(range);
    const messages = rows
      .filter(row => !row.isDeletedForMe)
      .map(row => this._rowToMessage(row))
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    
    // Cache the result
    this._messagesCache.set(conversationId, messages);
    
    return messages;
  }

  async latestMessage(conversationId) {
    // Check cache first
    if (this._messagesCache.has(conversationId)) {
      const cached = this._messagesCache.get(conversationId);
      if (cached.length > 0) {
        return cached[cached.length - 1];
      }
      return null;
    }

    const database = await this.getDb();
    const tx = database.transaction('messages', 'readonly');
    const store = tx.objectStore('messages');
    const index = store.index('conversationId_timestamp');
    
    const range = IDBKeyRange.bound(
      [conversationId, 0],
      [conversationId, Number.MAX_SAFE_INTEGER]
    );
    
    const rows = await index.getAll(range);
    const filtered = rows.filter(row => !row.isDeletedForMe);
    if (filtered.length === 0) return null;
    
    const last = filtered.reduce((a, b) => 
      a.timestamp > b.timestamp ? a : b
    );
    
    return this._rowToMessage(last);
  }

  async deleteMessageForMe(messageId) {
    const database = await this.getDb();
    const tx = database.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    
    const message = await store.get(messageId);
    if (message) {
      message.isDeletedForMe = 1;
      await store.put(message);
      
      // Update cache
      for (const [convId, messages] of this._messagesCache) {
        const msgIndex = messages.findIndex(m => m.id === messageId);
        if (msgIndex >= 0) {
          messages[msgIndex].isDeletedForMe = true;
          break;
        }
      }
    }
    
    await tx.done;
  }

  async deleteMessageForEveryone(messageId) {
    const database = await this.getDb();
    const tx = database.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    
    const message = await store.get(messageId);
    if (message) {
      message.isDeleted = 1;
      message.content = '';
      message.attachmentUrl = null;
      message.messageType = 'deleted';
      await store.put(message);
      
      // Update cache
      for (const [convId, messages] of this._messagesCache) {
        const msgIndex = messages.findIndex(m => m.id === messageId);
        if (msgIndex >= 0) {
          messages[msgIndex].isDeleted = true;
          messages[msgIndex].content = '';
          messages[msgIndex].attachmentUrl = null;
          messages[msgIndex].messageType = 'deleted';
          break;
        }
      }
    }
    
    await tx.done;
  }

  _messageToRow(m) {
    return {
      id: m.id,
      conversationId: m.conversationId,
      senderId: m.senderId,
      receiverId: m.receiverId,
      content: m.content,
      timestamp: m.timestamp.getTime(),
      status: m.status,
      messageType: m.messageType,
      attachmentUrl: m.attachmentUrl || null,
      attachmentType: m.attachmentType || null,
      attachmentName: m.attachmentName || null,
      attachmentSize: m.attachmentSize || null,
      audioDuration: m.audioDuration || null,
      imageWidth: m.imageWidth || null,
      imageHeight: m.imageHeight || null,
      replyToMessageId: m.replyToMessageId || null,
      replyToContent: m.replyToContent || null,
      replyToSenderId: m.replyToSenderId || null,
      replyToType: m.replyToType || null,
      replyToAttachmentUrl: m.replyToAttachmentUrl || null,
      seenBy: (m.seenBy || []).join(','),
      isDeleted: m.isDeleted ? 1 : 0,
      isDeletedForMe: m.isDeletedForMe ? 1 : 0,
    };
  }

  _rowToMessage(row) {
    return new ChatMessage({
      id: row.id,
      conversationId: row.conversationId,
      senderId: row.senderId,
      receiverId: row.receiverId,
      content: row.content,
      timestamp: new Date(row.timestamp),
      status: row.status,
      messageType: row.messageType,
      attachmentUrl: row.attachmentUrl || null,
      attachmentType: row.attachmentType || null,
      attachmentName: row.attachmentName || null,
      attachmentSize: row.attachmentSize || null,
      audioDuration: row.audioDuration || null,
      imageWidth: row.imageWidth || null,
      imageHeight: row.imageHeight || null,
      replyToMessageId: row.replyToMessageId || null,
      replyToContent: row.replyToContent || null,
      replyToSenderId: row.replyToSenderId || null,
      replyToType: row.replyToType || null,
      replyToAttachmentUrl: row.replyToAttachmentUrl || null,
      seenBy: row.seenBy ? row.seenBy.split(',').filter(s => s) : [],
      isDeleted: row.isDeleted === 1,
      isDeletedForMe: row.isDeletedForMe === 1,
    });
  }

  // ── Conversations ──────────────────────────────────────────────────────────

  async saveConversations(conversations) {
    if (!conversations || conversations.length === 0) return;
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');

    // Update cache
    for (const c of conversations) {
      const existingIndex = this._conversationsCache.findIndex(cc => cc.id === c.id);
      if (existingIndex >= 0) {
        this._conversationsCache[existingIndex] = c;
      } else {
        this._conversationsCache.push(c);
      }
    }

    for (const c of conversations) {
      const existing = await store.get(c.id);
      const existingIsDeleted = existing?.isDeleted || 0;
      const existingName = existing?.otherUserName || null;
      const existingAvatar = existing?.otherUserAvatar || null;

      await store.put({
        id: c.id,
        participants: (c.participants || []).join(','),
        otherUserId: c.otherUserId,
        lastMessage: c.lastMessage,
        lastMessageSenderId: c.lastMessageSenderId,
        lastMessageTime: c.lastMessageTime.getTime(),
        unreadCount: c.unreadCount,
        lastMessageType: c.lastMessageType,
        isArchived: c.isArchived ? 1 : 0,
        isMuted: c.isMuted ? 1 : 0,
        isPinned: c.isPinned ? 1 : 0,
        isDeleted: existingIsDeleted,
        otherUserName: c.otherUserName || existingName,
        otherUserAvatar: c.otherUserAvatar || existingAvatar,
        lastMessageStatus: c.lastMessageStatus || 'sent',
      });
    }

    await tx.done;
  }

  async updateConversationUserInfo({ conversationId, name, avatar }) {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    
    const conv = await store.get(conversationId);
    if (conv) {
      conv.otherUserName = name;
      if (avatar !== undefined) conv.otherUserAvatar = avatar;
      await store.put(conv);
      
      // Update cache
      const cacheIndex = this._conversationsCache.findIndex(c => c.id === conversationId);
      if (cacheIndex >= 0) {
        this._conversationsCache[cacheIndex].otherUserName = name;
        if (avatar !== undefined) {
          this._conversationsCache[cacheIndex].otherUserAvatar = avatar;
        }
      }
    }
    
    await tx.done;
  }

  async updateConversationLastMessageStatus(conversationId, status) {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    
    const conv = await store.get(conversationId);
    if (conv) {
      conv.lastMessageStatus = status;
      await store.put(conv);
      
      // Update cache
      const cacheIndex = this._conversationsCache.findIndex(c => c.id === conversationId);
      if (cacheIndex >= 0) {
        this._conversationsCache[cacheIndex].lastMessageStatus = status;
      }
    }
    
    await tx.done;
  }

  async loadConversations() {
    // Return cached data instantly if available
    if (this._conversationsCache.length > 0) {
      return this._conversationsCache.filter(c => !c.isDeleted && !c.isArchived)
        .sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime());
    }

    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readonly');
    const store = tx.objectStore('conversations');
    const index = store.index('lastMessageTime');
    
    const all = await index.getAll();
    const filtered = all
      .filter(row => row.isDeleted === 0 && row.isArchived === 0)
      .sort((a, b) => b.lastMessageTime - a.lastMessageTime);
    
    const conversations = filtered.map(row => this._rowToConversation(row));
    
    // Cache the result
    this._conversationsCache = conversations;
    
    return conversations;
  }

  async loadArchivedConversations() {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readonly');
    const store = tx.objectStore('conversations');
    const index = store.index('lastMessageTime');
    
    const all = await index.getAll();
    const filtered = all
      .filter(row => row.isDeleted === 0 && row.isArchived === 1)
      .sort((a, b) => b.lastMessageTime - a.lastMessageTime);
    
    return filtered.map(row => this._rowToConversation(row));
  }

  async archivedCount() {
    const conversations = await this.loadArchivedConversations();
    return conversations.length;
  }

  _rowToConversation(row) {
    const participantsRaw = row.participants || '';
    const participants = participantsRaw ? participantsRaw.split(',') : [];

    return new ChatConversation({
      id: row.id,
      participants: participants,
      otherUserId: row.otherUserId,
      lastMessage: row.lastMessage,
      lastMessageSenderId: row.lastMessageSenderId || '',
      lastMessageTime: new Date(row.lastMessageTime),
      unreadCount: row.unreadCount,
      lastMessageType: row.lastMessageType || 'text',
      lastMessageStatus: row.lastMessageStatus || 'sent',
      isArchived: row.isArchived === 1,
      isMuted: row.isMuted === 1,
      isPinned: row.isPinned === 1,
      otherUserName: row.otherUserName || null,
      otherUserAvatar: row.otherUserAvatar || null,
    });
  }

  // ── Mark conversation as read ──────────────────────────────────────────────

  async markConversationRead(conversationId) {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    
    const conv = await store.get(conversationId);
    if (conv) {
      conv.unreadCount = 0;
      await store.put(conv);
      
      // Update cache
      const cacheIndex = this._conversationsCache.findIndex(c => c.id === conversationId);
      if (cacheIndex >= 0) {
        this._conversationsCache[cacheIndex].unreadCount = 0;
      }
    }
    
    await tx.done;
  }

  // ── Archive flags ──────────────────────────────────────────────────────────

  async markConversationArchived(conversationId) {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    
    const conv = await store.get(conversationId);
    if (conv) {
      conv.isArchived = 1;
      await store.put(conv);
    }
    
    await tx.done;
  }

  async clearArchivedFlag(conversationId) {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    
    const conv = await store.get(conversationId);
    if (conv) {
      conv.isArchived = 0;
      await store.put(conv);
    }
    
    await tx.done;
  }

  // ── Delete / un-delete ─────────────────────────────────────────────────────

  async markConversationDeleted(conversationId) {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    
    const conv = await store.get(conversationId);
    if (conv) {
      conv.isDeleted = 1;
      await store.put(conv);
    }
    
    await tx.done;
  }

  async clearDeletedFlag(conversationId) {
    const database = await this.getDb();
    const tx = database.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    
    const conv = await store.get(conversationId);
    if (conv) {
      conv.isDeleted = 0;
      await store.put(conv);
    }
    
    await tx.done;
  }

  // ── Presence cache ─────────────────────────────────────────────────────────

  async savePresence({ uid, isOnline, lastSeenAt }) {
    const database = await this.getDb();
    const tx = database.transaction('user_presence', 'readwrite');
    const store = tx.objectStore('user_presence');
    
    await store.put({
      uid: uid,
      isOnline: isOnline ? 1 : 0,
      lastSeenAt: lastSeenAt ? lastSeenAt.getTime() : null,
      updatedAt: Date.now(),
    });
    
    await tx.done;
  }

  async loadPresence(uid) {
    const database = await this.getDb();
    const tx = database.transaction('user_presence', 'readonly');
    const store = tx.objectStore('user_presence');
    
    const row = await store.get(uid);
    if (!row) return null;
    
    return {
      isOnline: row.isOnline === 1,
      lastSeenAt: row.lastSeenAt ? new Date(row.lastSeenAt) : null,
    };
  }

  // ── Full wipe — call on logout ─────────────────────────────────────────────

  async clearAll() {
    const database = await this.getDb();
    const tx = database.transaction(['messages', 'conversations', 'user_presence'], 'readwrite');
    
    await tx.objectStore('messages').clear();
    await tx.objectStore('conversations').clear();
    await tx.objectStore('user_presence').clear();
    
    this._conversationsCache = [];
    this._messagesCache.clear();
    
    await tx.done;
  }

  // ── Cache invalidation ─────────────────────────────────────────────────────

  invalidateCache() {
    this._conversationsCache = [];
    this._messagesCache.clear();
  }

  // ── Preload conversations for instant display ─────────────────────────────

  async preloadConversations() {
    if (this._conversationsCache.length > 0) {
      return this._conversationsCache;
    }
    return await this.loadConversations();
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const chatLocalDb = ChatLocalDb.getInstance();
export { chatLocalDb };
export default chatLocalDb;